import React from 'react';
import { useT } from '../i18n';
import { USER_TYPES } from '../constants/designer';
import './UserTypeChoice.css';

// ── ACCOUNT TYPE ─────────────────────────────────────────────────────────────
// Shown once, between email verification and the details step. Cards rather
// than a dropdown: this decision changes what the rest of signup asks for, so it
// deserves to be legible at a glance — and each option can explain what it
// actually unlocks instead of being a bare label.
//
// Not a permanent choice: a customer can become a designer or a shop later from
// Settings, which is also the path a Google sign-in takes (OAuth never sees this
// step).
//
// The list is driven off USER_TYPES rather than hard-coded here, so a role can
// only ever be added in one place.
export default function UserTypeChoice({ value, onChange, disabled = false }) {
  const { t } = useT();

  const options = [
    {
      key: USER_TYPES.CUSTOMER,
      title: t('ut_customer'),
      desc: t('ut_customer_desc'),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M3 10.5 12 4l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-9.5Z"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
          <path d="M9.5 21v-6h5v6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
        </svg>
      ),
    },
    {
      key: USER_TYPES.DESIGNER,
      title: t('ut_designer'),
      desc: t('ut_designer_desc'),
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M12 3l1.9 4.6L18.5 9.5 13.9 11.4 12 16l-1.9-4.6L5.5 9.5l4.6-1.9L12 3Z"
            fill="currentColor"/>
          <path d="M18.5 15.5l.8 1.9 1.9.8-1.9.8-.8 1.9-.8-1.9-1.9-.8 1.9-.8.8-1.9Z"
            fill="currentColor" opacity="0.75"/>
        </svg>
      ),
    },
    {
      key: USER_TYPES.SHOP,
      title: t('ut_shop'),
      desc: t('ut_shop_desc'),
      // A storefront: awning, shutter line, door.
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path d="M4 9.5h16V20a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9.5Z"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
          <path d="M3.2 9.5 5 3.8h14l1.8 5.7"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
          <path d="M10 21v-5.5h4V21" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round"/>
        </svg>
      ),
    },
  ];

  return (
    <div className="utc" role="radiogroup" aria-label={t('ut_title')}>
      {options.map(o => {
        const on = value === o.key;
        return (
          <button
            key={o.key}
            type="button"
            role="radio"
            aria-checked={on}
            className={`utc-card${on ? ' utc-card--on' : ''}`}
            onClick={() => onChange(o.key)}
            disabled={disabled}
          >
            <span className="utc-icon">{o.icon}</span>
            <span className="utc-body">
              <span className="utc-title">{o.title}</span>
              <span className="utc-desc">{o.desc}</span>
            </span>
            <span className="utc-radio" aria-hidden="true" />
          </button>
        );
      })}
    </div>
  );
}
